/**
 * The reflection log & calendar (issue #217, epic #213 slice 4) — the
 * payoff surface: everything reflective, against time, in one place.
 *
 * WHAT IT SHOWS, and why it is a READ. Five kinds of entry come out of
 * `GET /api/reflection-log`, composed server-side from four tables that
 * each keep their own write path (`api/src/routes/reflectionLog.ts`). So
 * this component renders and links; it never saves. A match reflection is
 * still edited in the Match Reflections section below, a check-in in the
 * check-in section, coach feedback by the coach who wrote it. Adding a
 * composer here would be a second write path into somebody else's table.
 *
 * THE DEFAULT AT 390px IS THE LOG. #217 asks for this to be a deliberate
 * choice, so: a seven-column month grid on a 390px phone gives each day
 * roughly 48px, which is enough for a number and a dot and nothing that
 * says WHAT happened — and "what did I write" is the question this screen
 * exists to answer. The log answers it in the first line of every row. The
 * calendar earns its place at the other question — "how consistent have I
 * been" — which is about shape, not content, and reads fine as dots.
 *
 * #1222 (round 11, D240): the calendar also reads the player's matches
 * (played and planned), training sessions and goals — fetched when the
 * calendar is first opened, each on its own so one failure never blanks the
 * others or claims an empty day. The shapes, outlines and legend come from
 * `lib/calendarDays.js`; the kind chips belong to the log view only.
 *
 * The log is therefore the default at EVERY width, not just narrow ones.
 * A width-dependent default would mean the same player meets a different
 * screen on their phone than on the club laptop, and neither one is the
 * one they were taught. One default, one toggle, remembered nowhere.
 *
 * TAGGED WITHOUT RELYING ON COLOUR (#217, and #187's palette decision — in
 * this app green already means "on track" elsewhere). Every entry carries
 * an ICON and a WORD for its kind. Colour is a third, redundant channel:
 * remove it entirely and the screen still parses.
 *
 * CHECK-INS ARE IN THIS LOG BECAUSE IT IS THE PLAYER'S OWN. They are the
 * one source a linked coach's version of this same view does not contain
 * (#215) — enforced in the API, not here, and stated in the copy so a
 * player reading their whole history in one list does not conclude their
 * coach is reading the same list. `includes_checkins` on the response is
 * what says which log this is; the copy follows it rather than assuming.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { getWithRetry, isUnknownAnswer } from "../lib/fetchWithRetry";
import { SectionCard } from "./identityForm";
import AccordionCard from "./AccordionCard";
import { SecondaryAction } from "./kit/SecondaryAction";
import { clientDateHeaders, todayIso } from "../lib/trainingStatus";
import {
  CALENDAR_KINDS,
  CALENDAR_KIND_ORDER,
  buildCalendarDays,
  dayAriaLabel,
  dayHasContent,
  dayKinds,
  shortDate,
} from "../lib/calendarDays";
import {
  CalendarDays,
  CloudOff,
  Search,
  FileText,
  ChevronLeft,
  ChevronRight,
  ClipboardList,
  CalendarRange,
  Dumbbell,
  MessageSquare,
  NotebookPen,
  Sparkles,
  List,
} from "lucide-react";

/**
 * One row per entry kind the API can return. `label` is the word shown on
 * the tag, `short` the one-letter form the calendar's day cells use when
 * there is no room for a word (still paired with the icon's shape, never
 * colour alone). Keys match `LogEntryKind` in the API exactly — an unknown
 * kind falls back to a neutral tag rather than rendering nothing.
 */
const KINDS = {
  match_reflection: { label: "Match", short: "M", Icon: ClipboardList, tint: "#0B2E59", kitTint: "#0B2E59" },
  season_reflection: { label: "Season", short: "S", Icon: CalendarRange, tint: "#0B2E59", kitTint: "#0B2E59" },
  standalone_reflection: { label: "Note to self", short: "N", Icon: NotebookPen, tint: "#0B2E59", kitTint: "#0B2E59" },
  training_reflection: { label: "Training", short: "T", Icon: Dumbbell, tint: "#0B2E59", kitTint: "#0B2E59" },
  checkin: { label: "Check-in", short: "C", Icon: Sparkles, tint: "#8a6a10", kitTint: "#0B2E59" },
  coach_feedback: { label: "Coach feedback", short: "F", Icon: MessageSquare, tint: "#8a6a10", kitTint: "#0B2E59" },
};

const FALLBACK_KIND = { label: "Entry", short: "•", Icon: NotebookPen, tint: "#475569", kitTint: "#0B2E59" };

const kindOf = (kind) => KINDS[kind] || FALLBACK_KIND;

const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

/** `YYYY-MM` for a Date, without dragging in a date library. */
function monthKey(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

/** Days in a month grid, Monday-first, padded to whole weeks. */
function monthGrid(year, monthIndex) {
  const first = new Date(year, monthIndex, 1);
  const daysInMonth = new Date(year, monthIndex + 1, 0).getDate();
  // getDay(): 0 = Sunday. Monday-first is the week a cricket season is
  // planned in here, and matches the training list's ordering.
  const lead = (first.getDay() + 6) % 7;
  const cells = [];
  for (let i = 0; i < lead; i += 1) cells.push(null);
  for (let day = 1; day <= daysInMonth; day += 1) {
    cells.push(`${year}-${String(monthIndex + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`);
  }
  while (cells.length % 7 !== 0) cells.push(null);
  return cells;
}

const kitToggleStyle = (current) => ({
  minHeight: 44, padding: "0 14px", fontSize: 13, fontWeight: 600, border: "none",
  background: current ? "#0B2E59" : "#FFFFFF", color: current ? "#FFFFFF" : "#475569",
});

const kitChipStyle = (off) => ({
  minHeight: 44, padding: "6px 10px", borderRadius: 999, fontSize: 12, fontWeight: 600,
  border: `1px solid ${off ? "#E2E8F0" : "#0B2E59"}`,
  background: off ? "#FFFFFF" : "#E8F1FB",
  color: off ? "#64748B" : "#0B2E59",
});

const kitArrowStyle = {
  width: 44, height: 44, minHeight: 44, border: "1px solid #E2E8F0", borderRadius: 10, background: "#FFFFFF", color: "#475569",
};

function KindTag({ kind, kit = false }) {
  const { label, Icon, tint, kitTint } = kindOf(kind);
  if (kit) {
    // Round-10 board: every kind icon is navy; the word carries the kind.
    return (
      <span
        style={{
          display: "inline-flex", alignItems: "center", gap: 4, padding: "2px 8px", borderRadius: 999,
          background: "#F8FAFC", border: "1px solid #E2E8F0", fontSize: 11, fontWeight: 600, color: "#334155",
        }}
        data-testid={`rl-tag-${kind}`}
      >
        <Icon size={12} color={kitTint} aria-hidden="true" />
        {label}
      </span>
    );
  }
  return (
    <span
      className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[11px] font-semibold text-slate-700"
      data-testid={`rl-tag-${kind}`}
    >
      <Icon size={11} color={tint} aria-hidden="true" />
      {label}
    </span>
  );
}

/**
 * One entry. Collapsed to two lines; clicking opens it in place — #217's
 * "tapping an entry opens it", without a dialog. A dialog on a timeline
 * costs a dismiss for every entry read, and the harness would have one
 * more trigger to keep honest for no gain.
 */
function LogRow({ entry, open, onToggle, kit = false }) {
  if (kit) {
    return (
      <div
        style={{ background: "#FFFFFF", border: "1px solid #E2E8F0", borderRadius: 10 }}
        data-testid={`rl-entry-${entry.entry_id}`}
      >
        <button
          type="button"
          onClick={onToggle}
          className="w-full text-left min-w-0"
          style={{ padding: 12, display: "flex", flexDirection: "column", gap: 4, background: "none", border: "none" }}
          data-testid={`rl-open-${entry.entry_id}`}
          aria-expanded={open}
        >
          <span className="flex items-center gap-2 flex-wrap">
            <KindTag kind={entry.kind} kit />
            <span style={{ fontSize: 12, color: "#64748B" }}>{entry.date}</span>
          </span>
          <span className="break-words" style={{ fontSize: 14, fontWeight: 600, color: "#0F172A" }}>{entry.title}</span>
          <span
            className={`break-words ${open ? "whitespace-pre-wrap" : "line-clamp-2"}`}
            style={{ fontSize: 14, color: "#475569", display: open ? "block" : undefined }}
          >
            {entry.body || <span className="italic" style={{ color: "#64748B" }}>No text on this one.</span>}
          </span>
          {entry.author === "coach" && (
            <span style={{ fontSize: 12, color: "#64748B" }}>From {entry.author_name || "your coach"}</span>
          )}
        </button>
      </div>
    );
  }
  return (
    <div className="rounded-lg border border-slate-200" data-testid={`rl-entry-${entry.entry_id}`}>
      <button
        type="button"
        onClick={onToggle}
        className="w-full text-left p-3 min-w-0"
        data-testid={`rl-open-${entry.entry_id}`}
        aria-expanded={open}
      >
        <div className="flex items-start justify-between gap-3 min-w-0">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <KindTag kind={entry.kind} />
              <span className="text-xs font-mono text-slate-500">{entry.date}</span>
            </div>
            <div className="text-sm font-semibold text-slate-900 mt-1 break-words">{entry.title}</div>
            <p
              className={`text-sm text-slate-600 mt-1 break-words ${open ? "whitespace-pre-wrap" : "line-clamp-2"}`}
            >
              {entry.body || <span className="italic text-slate-400">No text on this one.</span>}
            </p>
            {entry.author === "coach" && (
              <div className="text-xs text-slate-500 mt-1">
                From {entry.author_name || "your coach"}
              </div>
            )}
          </div>
        </div>
      </button>
    </div>
  );
}

/** The calendar's own three reads (#1222). Each fails and retries on its own (D57). */
const CAL_SOURCES = {
  matches: { url: "/matches?status=all", noun: "matches" },
  sessions: { url: "/training-sessions", noun: "training" },
  goals: { url: "/goals", noun: "goals" },
};

const NAVY = "#0B2E59";
const PLANNED_INK = "#475569";

/** One calendar glyph from the shared table in `lib/calendarDays.js`. */
function KindGlyph({ kind, size = 11, planned = false }) {
  return (
    <svg
      width={size} height={size} viewBox="0 0 24 24" fill="none"
      stroke={planned ? PLANNED_INK : NAVY} strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"
      style={{ flexShrink: 0 }} aria-hidden="true" data-glyph={kind}
    >
      {CALENDAR_KINDS[kind].paths.map((d) => <path key={d} d={d} />)}
    </svg>
  );
}

const legendWord = { display: "inline-flex", alignItems: "center", gap: 5, fontSize: 12, fontWeight: 600, color: "#0F172A" };
const legendKey = { display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12, color: "#475569" };

function CalendarLegend() {
  return (
    <div
      data-testid="rl-legend"
      style={{ display: "flex", flexDirection: "column", gap: 8, padding: "10px 12px", background: "#F8FAFC", border: "1px solid #E2E8F0", borderRadius: 10 }}
    >
      <div style={{ display: "flex", flexWrap: "wrap", gap: "6px 14px" }}>
        {CALENDAR_KIND_ORDER.map((kind) => (
          <span key={kind} style={legendWord}><KindGlyph kind={kind} size={12} />{CALENDAR_KINDS[kind].label}</span>
        ))}
        <span style={legendWord}>
          <span aria-hidden="true" style={{ width: 5, height: 5, borderRadius: 999, background: NAVY, flexShrink: 0 }} />
          Something written
        </span>
      </div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: "6px 14px" }}>
        <span style={legendKey}>
          <span aria-hidden="true" style={{ width: 14, height: 14, boxSizing: "border-box", border: "1.5px solid #CBD5E1", background: "#FFFFFF" }} />
          Done
        </span>
        <span style={legendKey}>
          <span aria-hidden="true" style={{ width: 14, height: 14, boxSizing: "border-box", border: "1.5px dashed #94A3B8", background: "#FFFFFF" }} />
          Planned
        </span>
        <span style={legendKey}>
          <span aria-hidden="true" style={{ width: 14, height: 14, borderRadius: 999, background: NAVY }} />
          Today
        </span>
      </div>
    </div>
  );
}

/** One day button. Shapes and outlines carry done/planned/today; colour is redundant. */
function CalendarDay({ day, info, today, selected, onToggle }) {
  const has = dayHasContent(info);
  const isToday = day === today;
  const kinds = dayKinds(info);
  const outline = selected
    ? `2px solid ${NAVY}`
    : info?.state === "planned" ? "1.5px dashed #94A3B8" : info?.state === "done" ? "1.5px solid #CBD5E1" : "none";
  return (
    <button
      type="button"
      onClick={onToggle}
      disabled={!has}
      aria-pressed={has ? selected : undefined}
      aria-label={dayAriaLabel(day, info, today)}
      data-testid={`rl-day-${day}`}
      data-day-state={info?.state || "empty"}
      data-today={isToday ? "true" : undefined}
      className="min-w-0"
      style={{
        display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "flex-start", gap: 2,
        minHeight: 44, aspectRatio: "1", padding: "4px 0 0", boxSizing: "border-box", border: "none", borderRadius: 0,
        fontSize: 12, fontWeight: 600,
        background: has ? "#FFFFFF" : "#F8FAFC",
        color: has ? "#0F172A" : "#94A3B8",
        outline, outlineOffset: -4,
      }}
    >
      <span
        style={{
          display: "inline-flex", alignItems: "center", justifyContent: "center", height: 20,
          ...(isToday ? { width: 20, borderRadius: 999, background: NAVY, color: "#FFFFFF" } : null),
        }}
      >
        {Number(day.slice(8))}
      </span>
      <span style={{ display: "flex", alignItems: "center", gap: 2, height: 11 }}>
        {kinds.map(({ kind, planned }) => <KindGlyph key={kind} kind={kind} size={11} planned={planned} />)}
        {info?.written.length > 0 && (
          <span aria-hidden="true" data-written="true" style={{ width: 5, height: 5, borderRadius: 999, background: NAVY, flexShrink: 0 }} />
        )}
      </span>
    </button>
  );
}

/** A match, training session or goal on the selected day, in the board's words. */
function CalendarItemRow({ item }) {
  const def = CALENDAR_KINDS[item.kind];
  const label = item.kind === "goal" ? (item.reached ? def.doneLabel : def.plannedLabel) : item.planned ? def.plannedLabel : def.label;
  // Only the two sentences the board draws (D287): a goal marked done on the
  // day, and a planned match.
  const note =
    item.kind === "goal" && item.reached && item.onAchievedDay
      ? "Marked done on this day."
      : item.kind === "match" && item.planned
        ? "Planned in Coming up. After the day, it asks how it went."
        : item.reflection
          ? item.reflection.body || null
          : null;
  return (
    <div
      data-testid={`rl-item-${item.kind}-${item.id}`}
      data-planned={item.planned ? "true" : "false"}
      style={{
        display: "flex", flexDirection: "column", gap: 4, padding: 12, background: "#FFFFFF",
        border: item.planned ? "1px dashed #94A3B8" : "1px solid #E2E8F0", borderRadius: 10,
      }}
    >
      <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <span
          style={{
            display: "inline-flex", alignItems: "center", gap: 4, padding: "2px 8px", borderRadius: 999,
            background: "#F8FAFC", border: "1px solid #E2E8F0", fontSize: 11, fontWeight: 600, color: "#0F172A",
          }}
        >
          <KindGlyph kind={item.kind} size={11} planned={item.planned} />
          {label}
        </span>
        <span style={{ fontSize: 12, color: "#64748B" }}>{item.date}</span>
      </span>
      <span className="break-words" style={{ fontSize: 14, fontWeight: 600, color: "#0F172A" }}>{item.title}</span>
      {note && (
        <span className="break-words whitespace-pre-wrap" style={{ fontSize: 14, lineHeight: 1.5, color: "#475569" }}>{note}</span>
      )}
    </div>
  );
}

export default function ReflectionLog({ kit = false }) {
  const [entries, setEntries] = useState([]);
  const [includesCheckins, setIncludesCheckins] = useState(true);
  const [loaded, setLoaded] = useState(false);
  const [failed, setFailed] = useState(false);
  // "loading" until the first answer, then "failed" | "empty" | "ready".
  // Only the kit leg renders by it; the old look keeps its own flags.
  const status = !loaded ? "loading" : failed ? "failed" : entries.length === 0 ? "empty" : "ready";
  const [view, setView] = useState("log"); // see this file's header
  const [hidden, setHidden] = useState([]); // kinds toggled OFF
  const [openId, setOpenId] = useState(null);
  const [cursor, setCursor] = useState(() => new Date());
  const [selectedDay, setSelectedDay] = useState(null);
  // The calendar's three extra reads (#1222), fetched when the calendar is
  // first opened, never on mount: the log view costs nothing extra. Each is
  // { status: "loading" | "ok" | "failed", data: [] }.
  const [cal, setCal] = useState({});
  const requested = useRef(new Set());

  const loadSource = useCallback((key) => {
    const { url } = CAL_SOURCES[key];
    setCal((prev) => ({ ...prev, [key]: { status: "loading", data: [] } }));
    const opts = key === "sessions" ? { headers: clientDateHeaders() } : undefined;
    getWithRetry(url, opts)
      .then((r) => setCal((prev) => ({ ...prev, [key]: { status: "ok", data: Array.isArray(r.data) ? r.data : [] } })))
      .catch((e) => {
        // A deterministic 4xx is an answer about the caller (the same rule as
        // the log read above); only a genuine unknown is a failure to show.
        setCal((prev) => ({ ...prev, [key]: isUnknownAnswer(e) ? { status: "failed", data: [] } : { status: "ok", data: [] } }));
        console.error(`ReflectionLog: failed to load ${url} for the calendar`, e);
      });
  }, []);

  const load = useCallback(() => {
    // Issue #271's rule, and the same idiom as this page's route parent
    // (`DevelopmentPlan.jsx`'s `load`/`loadFeedback`): retry what a retry
    // can fix, and only render the failure state for a genuine unknown.
    //
    // `isUnknownAnswer` is not optional here. `GET /reflection-log` with no
    // `player_id` is a deterministic 400 for a coach caller (`resolveScope`;
    // since #1166 `/development` is a player's alone, but the API still
    // answers that way). That is an ANSWER about who is asking, not a
    // transport failure, so it renders the neutral empty state rather than
    // "reload the page to try again" — a button that could never work.
    getWithRetry("/reflection-log")
      .then((r) => {
        setEntries(r.data?.entries || []);
        setIncludesCheckins(r.data?.includes_checkins !== false);
        setFailed(false);
        setLoaded(true);
      })
      .catch((e) => {
        setFailed(isUnknownAnswer(e));
        setLoaded(true);
        console.error("ReflectionLog: failed to load /reflection-log", e);
      });
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    // The new look also reads them while the log is empty (D286), to know
    // whether the Log / Calendar toggle has anything to offer.
    if (!kit || (view !== "calendar" && status !== "empty")) return;
    for (const key of Object.keys(CAL_SOURCES)) {
      if (!requested.current.has(key)) {
        requested.current.add(key);
        loadSource(key);
      }
    }
  }, [kit, view, status, loadSource]);

  const retry = () => {
    setLoaded(false);
    setFailed(false);
    load();
  };

  const counts = useMemo(() => {
    const out = {};
    for (const e of entries) out[e.kind] = (out[e.kind] || 0) + 1;
    return out;
  }, [entries]);

  const visible = useMemo(
    () => entries.filter((e) => !hidden.includes(e.kind)),
    [entries, hidden],
  );

  const byDay = useMemo(() => {
    const out = new Map();
    for (const e of visible) {
      if (!out.has(e.date)) out.set(e.date, []);
      out.get(e.date).push(e);
    }
    return out;
  }, [visible]);

  const today = todayIso();
  const calDays = useMemo(
    () => buildCalendarDays({
      entries,
      matches: cal.matches?.data,
      sessions: cal.sessions?.data,
      goals: cal.goals?.data,
      today,
    }),
    [entries, cal, today],
  );
  const calHasAny = calDays.size > 0;
  const calFailedAny = Object.keys(CAL_SOURCES).some((k) => cal[k]?.status === "failed");
  // D286 / D57: with an empty log the toggle is offered when the calendar has
  // something, or when a source failed (never a silent disappearance).
  const emptyCalendar = status === "empty" && (calHasAny || calFailedAny);
  const calFailed = Object.keys(CAL_SOURCES).filter((k) => cal[k]?.status === "failed");
  const calLoading = Object.keys(CAL_SOURCES).some((k) => !cal[k] || cal[k].status === "loading");

  const toggleKind = (kind) =>
    setHidden((prev) => (prev.includes(kind) ? prev.filter((k) => k !== kind) : [...prev, kind]));

  const shown = selectedDay ? byDay.get(selectedDay) || [] : visible;
  const grid = monthGrid(cursor.getFullYear(), cursor.getMonth());
  const cursorMonth = monthKey(cursor);

  const stepMonth = (delta) => {
    setSelectedDay(null);
    setCursor((d) => new Date(d.getFullYear(), d.getMonth() + delta, 1));
  };

  /* Issue #378: this is the section Martin raised three separate ways —
     "your reflection log is going to get very large". Folded by default,
     with the total on the header so a player can see there is something in
     there without opening it. The count is `entries.length`, not the
     filtered `visible.length`: while the card is shut the filter chips are
     not on screen, so a filtered number would be a figure with no visible
     cause. `rl-count` inside the panel still reports "n of m shown". */
  const kitBody = (
    <div className="space-y-3" data-testid="rl-section">
      <div
        className={kit ? "flex items-start gap-2" : "flex items-start gap-2 rounded-lg bg-slate-50 border border-slate-200 p-3 text-sm text-slate-600"}
        style={kit ? { background: "#E8F1FB", borderRadius: 10, padding: "10px 12px", fontSize: 13, color: "#0F172A" } : undefined}
        data-testid="rl-scope-note"
      >
        <NotebookPen size={14} className="mt-0.5 shrink-0 text-[#0B2E59]" />
        <span>
          {includesCheckins ? (
            <>
              This is <b>your</b> log — it includes your monthly check-ins. A coach you are
              linked to sees your match, season and training reflections, and their own feedback.{" "}
              <b>They never see your check-ins.</b>
            </>
          ) : (
            <>
              This player's match, season and training reflections, alongside your feedback. Their
              monthly check-ins are <b>not shown to anyone but them</b>.
            </>
          )}
        </span>
      </div>

      {kit && status === "loading" && (
        <div className="flex flex-col gap-2" data-testid="rl-loading" aria-busy="true">
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              style={{
                height: 76, background: "#FFFFFF", border: "1px solid #E2E8F0", borderRadius: 12,
                padding: 14, display: "flex", flexDirection: "column", gap: 10, justifyContent: "center",
              }}
            >
              <span style={{ height: 10, width: "55%", borderRadius: 6, background: "#E2E8F0" }} />
              <span style={{ height: 10, width: "80%", borderRadius: 6, background: "#E2E8F0" }} />
            </div>
          ))}
        </div>
      )}

      {kit && status === "failed" && (
        <div
          style={{
            background: "#FFFFFF", border: "1px solid #E2E8F0", borderRadius: 12, padding: 16,
            display: "flex", flexDirection: "column", gap: 8, alignItems: "flex-start",
          }}
          data-testid="rl-failed"
        >
          <CloudOff size={20} color="#B42318" aria-hidden="true" />
          <div style={{ fontSize: 15, fontWeight: 600, color: "#0F172A" }}>Couldn't load your log just now.</div>
          <div style={{ fontSize: 12, color: "#64748B" }}>Everything you've written is still safe.</div>
          <SecondaryAction data-testid="rl-failed-retry" onClick={retry} style={{ minHeight: 44 }}>
            Try again
          </SecondaryAction>
        </div>
      )}

      {(status === "ready" || emptyCalendar) && (<>
      {/* View toggle. Plain buttons rather than Radix Tabs: this is one
          control over one list, and the harness reads tab state from the
          ROUTE's source file (frontend/e2e/tabs.js), where a component-
          level <Tabs> would be invisible anyway. */}
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div
          className={kit ? "inline-flex overflow-hidden" : "inline-flex rounded-lg border border-slate-200 overflow-hidden"}
          style={kit ? { border: "1px solid #E2E8F0", borderRadius: 10 } : undefined}
          role="group"
        >
          <button
            type="button"
            onClick={() => { setView("log"); setSelectedDay(null); }}
            className={kit ? "inline-flex items-center gap-1.5" : `inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold ${view === "log" ? "bg-[#0B2E59] text-white" : "bg-white text-slate-600"}`}
            style={kit ? kitToggleStyle(view === "log") : undefined}
            data-testid="rl-view-log"
            aria-pressed={view === "log"}
          >
            <List size={kit ? 14 : 13} /> Log
          </button>
          <button
            type="button"
            onClick={() => setView("calendar")}
            className={kit ? "inline-flex items-center gap-1.5" : `inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold ${view === "calendar" ? "bg-[#0B2E59] text-white" : "bg-white text-slate-600"}`}
            style={kit ? kitToggleStyle(view === "calendar") : undefined}
            data-testid="rl-view-calendar"
            aria-pressed={view === "calendar"}
          >
            <CalendarDays size={kit ? 14 : 13} /> Calendar
          </button>
        </div>
        {(status === "ready" || view === "calendar") && (
        <span className="text-xs text-slate-500" data-testid="rl-count">
          {view === "calendar" ? "Back and ahead" : `${visible.length} of ${entries.length} shown`}
        </span>
        )}
      </div>

      {/* Filter by type. Every chip carries its word and its icon, so the
          on/off state is legible from the fill AND from the strikethrough-
          free label plus the count — never from hue alone. */}
      {view === "log" && status === "ready" && (
      <div className={kit ? "flex flex-wrap" : "flex flex-wrap gap-2"} style={kit ? { gap: 6 } : undefined} data-testid="rl-filters">
        {Object.keys(KINDS).map((kind) => {
          const { label, Icon } = KINDS[kind];
          const off = hidden.includes(kind);
          return (
            <button
              key={kind}
              type="button"
              onClick={() => toggleKind(kind)}
              className={kit ? "inline-flex items-center gap-1.5" : `inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-semibold transition ${
                off
                  ? "border-slate-200 bg-white text-slate-400"
                  : "border-[#0B2E59] bg-[#0B2E5910] text-[#0B2E59]"
              }`}
              style={kit ? kitChipStyle(off) : undefined}
              data-testid={`rl-filter-${kind}`}
              aria-pressed={!off}
            >
              <Icon size={12} aria-hidden="true" />
              {label} ({counts[kind] || 0})
              {off && (kit ? " · off" : <span className="sr-only"> — hidden</span>)}
            </button>
          );
        })}
      </div>
      )}

      {!kit && failed && (
        <p className="text-sm text-slate-500" data-testid="rl-failed">
          Couldn't load your log just now. Everything you've written is still safe — reload the page
          to try again.
        </p>
      )}

      {!kit && !failed && loaded && entries.length === 0 && (
        <p className="text-sm text-slate-500" data-testid="rl-empty">
          Nothing here yet. Write a check-in below, reflect on a match after you log it, or add a
          training reflection — whatever you write shows up here, on its date.
        </p>
      )}

      {!failed && view === "calendar" && (() => {
        const pad = kit ? 14 : 0;
        const monthHasContent = grid.some((d) => d && dayHasContent(calDays.get(d)));
        const nothingToSay = !monthHasContent && calFailed.length === 0 && !calLoading;
        return (
        <div
          className="flex flex-col"
          style={{ gap: 12, margin: kit ? "0 -14px" : undefined }}
          data-testid="rl-calendar"
        >
          {calLoading && (
            <div style={{ padding: `0 ${pad}px` }} data-testid="rl-cal-loading" aria-busy="true">
              <div style={{ height: 76, background: "#FFFFFF", border: "1px solid #E2E8F0", borderRadius: 12, padding: 14, display: "flex", flexDirection: "column", gap: 10, justifyContent: "center" }}>
                <span style={{ height: 10, width: "55%", borderRadius: 6, background: "#E2E8F0" }} />
                <span style={{ height: 10, width: "80%", borderRadius: 6, background: "#E2E8F0" }} />
              </div>
            </div>
          )}
          {calFailed.map((key) => (
            <div
              key={key}
              style={{ margin: `0 ${pad}px`, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, flexWrap: "wrap", fontSize: 13, color: "#0F172A" }}
              data-testid={`rl-cal-failed-${key}`}
            >
              <span>Couldn't load your {CAL_SOURCES[key].noun} for the calendar.</span>
              <SecondaryAction
                data-testid={`rl-cal-retry-${key}`}
                onClick={() => loadSource(key)}
                style={{ minHeight: 44 }}
              >
                Try again
              </SecondaryAction>
            </div>
          ))}

          {!nothingToSay && <div style={{ padding: `0 ${pad}px` }}><CalendarLegend /></div>}

          <div style={{ padding: `0 ${pad}px` }} className="flex items-center justify-between gap-2">
            <button
              type="button"
              onClick={() => stepMonth(-1)}
              className={kit ? "inline-flex items-center justify-center" : "p-1.5 rounded-lg border border-slate-200 text-slate-600"}
              style={kit ? kitArrowStyle : undefined}
              data-testid="rl-prev-month"
              aria-label="Previous month"
            >
              <ChevronLeft size={14} />
            </button>
            <div className="text-sm font-semibold text-slate-900" style={kit ? { fontSize: 15, fontWeight: 700, color: "#0F172A" } : undefined} data-testid="rl-month">
              {MONTH_NAMES[cursor.getMonth()]} {cursor.getFullYear()}
            </div>
            <button
              type="button"
              onClick={() => stepMonth(1)}
              className={kit ? "inline-flex items-center justify-center" : "p-1.5 rounded-lg border border-slate-200 text-slate-600"}
              style={kit ? kitArrowStyle : undefined}
              data-testid="rl-next-month"
              aria-label="Next month"
            >
              <ChevronRight size={14} />
            </button>
          </div>

          {nothingToSay ? (
            <div
              data-testid="rl-calendar-empty"
              style={{
                margin: `0 ${pad}px`, display: "flex", flexDirection: "column", alignItems: "center", gap: 10, padding: "28px 20px",
                background: "#FFFFFF", border: "1px solid #E2E8F0", borderRadius: 12, textAlign: "center",
              }}
            >
              <span
                aria-hidden="true"
                style={{ width: 52, height: 52, borderRadius: 16, background: "#E8F1FB", display: "inline-flex", alignItems: "center", justifyContent: "center" }}
              >
                <Search size={22} color={NAVY} />
              </span>
              <div style={{ fontSize: 15, fontWeight: 600, color: "#0F172A" }}>Nothing in {MONTH_NAMES[cursor.getMonth()]} yet.</div>
              <div style={{ fontSize: 14, lineHeight: 1.5, color: "#475569" }}>
                Matches, training and goals you plan show here as you add them.
              </div>
            </div>
          ) : (
            <>
              <div
                className="grid"
                style={{
                  gridTemplateColumns: "repeat(7, minmax(0, 1fr))", gap: 1, background: "#E2E8F0",
                  borderTop: "1px solid #E2E8F0", borderBottom: "1px solid #E2E8F0",
                }}
                data-testid="rl-grid"
              >
                {["M", "T", "W", "T", "F", "S", "S"].map((d, i) => (
                  <span
                    key={`${d}-${i}`}
                    className="min-w-0"
                    style={{ textAlign: "center", padding: "4px 0", fontSize: 10, fontWeight: 700, letterSpacing: "0.1em", color: "#64748B", background: "#FFFFFF" }}
                  >
                    {d}
                  </span>
                ))}
                {grid.map((day, i) => {
                  if (!day) return <span key={`pad-${i}`} className="min-w-0" style={{ background: "#F8FAFC" }} />;
                  return (
                    <CalendarDay
                      key={day}
                      day={day}
                      info={calDays.get(day)}
                      today={today}
                      selected={selectedDay === day}
                      onToggle={() => setSelectedDay(selectedDay === day ? null : day)}
                    />
                  );
                })}
              </div>

              <div style={{ padding: `0 ${pad}px`, fontSize: 12, lineHeight: 1.5, color: "#64748B" }} data-testid="rl-calendar-hint">
                {selectedDay
                  ? `Showing ${shortDate(selectedDay)}. Tap the day again to close it.`
                  : "Tap a day to see what happened, or what is planned."}
              </div>
            </>
          )}
          <span className="sr-only" data-testid="rl-month-key">{cursorMonth}</span>
        </div>
        );
      })()}

      {!failed && view === "calendar" && selectedDay && (() => {
        const info = calDays.get(selectedDay);
        const items = info ? [...info.matches, ...info.training, ...info.goals] : [];
        return (
          <div className="space-y-2" data-testid="rl-list">
            {(info?.listed || []).map((entry) => (
              <LogRow
                key={entry.entry_id}
                entry={entry}
                kit={kit}
                open={openId === entry.entry_id}
                onToggle={() => setOpenId(openId === entry.entry_id ? null : entry.entry_id)}
              />
            ))}
            {items.map((item) => <CalendarItemRow key={`${item.kind}-${item.id}`} item={item} />)}
          </div>
        );
      })()}

      {!failed && view === "log" && entries.length > 0 && (
        <div className="space-y-2" data-testid="rl-list">
          {shown.length === 0 && (
            <p className="text-sm text-slate-500" data-testid="rl-filtered-empty">
              Nothing matches those filters. Turn one back on above.
            </p>
          )}
          {shown.map((entry) => (
            <LogRow
              key={entry.entry_id}
              entry={entry}
              kit={kit}
              open={openId === entry.entry_id}
              onToggle={() => setOpenId(openId === entry.entry_id ? null : entry.entry_id)}
            />
          ))}
        </div>
      )}
      </>)}

      {status === "empty" && !(emptyCalendar && view === "calendar") && (
        <div
          style={{
            background: "#FFFFFF", border: "1px solid #E2E8F0", borderRadius: 12, padding: 16,
            display: "flex", flexDirection: "column", gap: 8, alignItems: "center", textAlign: "center",
          }}
          data-testid="rl-empty"
        >
          <span
            aria-hidden="true"
            style={{ width: 52, height: 52, borderRadius: 12, background: "#E8F1FB", display: "inline-flex", alignItems: "center", justifyContent: "center" }}
          >
            <FileText size={24} color="#0B2E59" />
          </span>
          <div style={{ fontSize: 15, fontWeight: 600, color: "#0F172A" }}>Nothing here yet.</div>
          <div style={{ fontSize: 12, color: "#64748B" }}>
            Write a check-in below, reflect on a match after you log it, or add a training reflection — whatever you write shows up here, on its date.
          </div>
        </div>
      )}
    </div>
  );

  const oldBody = (
    <div className="space-y-3" data-testid="rl-section">
      <div
        className={kit ? "flex items-start gap-2" : "flex items-start gap-2 rounded-lg bg-slate-50 border border-slate-200 p-3 text-sm text-slate-600"}
        style={kit ? { background: "#E8F1FB", borderRadius: 10, padding: "10px 12px", fontSize: 13, color: "#0F172A" } : undefined}
        data-testid="rl-scope-note"
      >
        <NotebookPen size={14} className="mt-0.5 shrink-0 text-[#0B2E59]" />
        <span>
          {includesCheckins ? (
            <>
              This is <b>your</b> log — it includes your monthly check-ins. A coach you are
              linked to sees your match, season and training reflections, and their own feedback.{" "}
              <b>They never see your check-ins.</b>
            </>
          ) : (
            <>
              This player's match, season and training reflections, alongside your feedback. Their
              monthly check-ins are <b>not shown to anyone but them</b>.
            </>
          )}
        </span>
      </div>

      {kit && status === "loading" && (
        <div className="flex flex-col gap-2" data-testid="rl-loading" aria-busy="true">
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              style={{
                height: 76, background: "#FFFFFF", border: "1px solid #E2E8F0", borderRadius: 12,
                padding: 14, display: "flex", flexDirection: "column", gap: 10, justifyContent: "center",
              }}
            >
              <span style={{ height: 10, width: "55%", borderRadius: 6, background: "#E2E8F0" }} />
              <span style={{ height: 10, width: "80%", borderRadius: 6, background: "#E2E8F0" }} />
            </div>
          ))}
        </div>
      )}

      {kit && status === "failed" && (
        <div
          style={{
            background: "#FFFFFF", border: "1px solid #E2E8F0", borderRadius: 12, padding: 16,
            display: "flex", flexDirection: "column", gap: 8, alignItems: "flex-start",
          }}
          data-testid="rl-failed"
        >
          <CloudOff size={20} color="#B42318" aria-hidden="true" />
          <div style={{ fontSize: 15, fontWeight: 600, color: "#0F172A" }}>Couldn't load your log just now.</div>
          <div style={{ fontSize: 12, color: "#64748B" }}>Everything you've written is still safe.</div>
          <SecondaryAction data-testid="rl-failed-retry" onClick={retry} style={{ minHeight: 44 }}>
            Try again
          </SecondaryAction>
        </div>
      )}

      {kit && status === "empty" && (
        <div
          style={{
            background: "#FFFFFF", border: "1px solid #E2E8F0", borderRadius: 12, padding: 16,
            display: "flex", flexDirection: "column", gap: 8, alignItems: "center", textAlign: "center",
          }}
          data-testid="rl-empty"
        >
          <span
            aria-hidden="true"
            style={{ width: 52, height: 52, borderRadius: 12, background: "#E8F1FB", display: "inline-flex", alignItems: "center", justifyContent: "center" }}
          >
            <FileText size={24} color="#0B2E59" />
          </span>
          <div style={{ fontSize: 15, fontWeight: 600, color: "#0F172A" }}>Nothing here yet.</div>
          <div style={{ fontSize: 12, color: "#64748B" }}>
            Write a check-in below, reflect on a match after you log it, or add a training reflection — whatever you write shows up here, on its date.
          </div>
        </div>
      )}

      {(!kit || status === "ready") && (<>
      {/* View toggle. Plain buttons rather than Radix Tabs: this is one
          control over one list, and the harness reads tab state from the
          ROUTE's source file (frontend/e2e/tabs.js), where a component-
          level <Tabs> would be invisible anyway. */}
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div
          className={kit ? "inline-flex overflow-hidden" : "inline-flex rounded-lg border border-slate-200 overflow-hidden"}
          style={kit ? { border: "1px solid #E2E8F0", borderRadius: 10 } : undefined}
          role="group"
        >
          <button
            type="button"
            onClick={() => { setView("log"); setSelectedDay(null); }}
            className={kit ? "inline-flex items-center gap-1.5" : `inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold ${view === "log" ? "bg-[#0B2E59] text-white" : "bg-white text-slate-600"}`}
            style={kit ? kitToggleStyle(view === "log") : undefined}
            data-testid="rl-view-log"
            aria-pressed={view === "log"}
          >
            <List size={kit ? 14 : 13} /> Log
          </button>
          <button
            type="button"
            onClick={() => setView("calendar")}
            className={kit ? "inline-flex items-center gap-1.5" : `inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold ${view === "calendar" ? "bg-[#0B2E59] text-white" : "bg-white text-slate-600"}`}
            style={kit ? kitToggleStyle(view === "calendar") : undefined}
            data-testid="rl-view-calendar"
            aria-pressed={view === "calendar"}
          >
            <CalendarDays size={kit ? 14 : 13} /> Calendar
          </button>
        </div>
        <span className="text-xs text-slate-500" data-testid="rl-count">
          {visible.length} of {entries.length} shown
        </span>
      </div>

      {/* Filter by type. Every chip carries its word and its icon, so the
          on/off state is legible from the fill AND from the strikethrough-
          free label plus the count — never from hue alone. */}
      <div className={kit ? "flex flex-wrap" : "flex flex-wrap gap-2"} style={kit ? { gap: 6 } : undefined} data-testid="rl-filters">
        {Object.keys(KINDS).map((kind) => {
          const { label, Icon } = KINDS[kind];
          const off = hidden.includes(kind);
          return (
            <button
              key={kind}
              type="button"
              onClick={() => toggleKind(kind)}
              className={kit ? "inline-flex items-center gap-1.5" : `inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-semibold transition ${
                off
                  ? "border-slate-200 bg-white text-slate-400"
                  : "border-[#0B2E59] bg-[#0B2E5910] text-[#0B2E59]"
              }`}
              style={kit ? kitChipStyle(off) : undefined}
              data-testid={`rl-filter-${kind}`}
              aria-pressed={!off}
            >
              <Icon size={12} aria-hidden="true" />
              {label} ({counts[kind] || 0})
              {off && (kit ? " · off" : <span className="sr-only"> — hidden</span>)}
            </button>
          );
        })}
      </div>

      {!kit && failed && (
        <p className="text-sm text-slate-500" data-testid="rl-failed">
          Couldn't load your log just now. Everything you've written is still safe — reload the page
          to try again.
        </p>
      )}

      {!kit && !failed && loaded && entries.length === 0 && (
        <p className="text-sm text-slate-500" data-testid="rl-empty">
          Nothing here yet. Write a check-in below, reflect on a match after you log it, or add a
          training reflection — whatever you write shows up here, on its date.
        </p>
      )}

      {!failed && entries.length > 0 && view === "calendar" && (
        <div className="space-y-2" data-testid="rl-calendar">
          <div className="flex items-center justify-between gap-2">
            <button
              type="button"
              onClick={() => stepMonth(-1)}
              className={kit ? "inline-flex items-center justify-center" : "p-1.5 rounded-lg border border-slate-200 text-slate-600"}
              style={kit ? kitArrowStyle : undefined}
              data-testid="rl-prev-month"
              aria-label="Previous month"
            >
              <ChevronLeft size={14} />
            </button>
            <div className="text-sm font-semibold text-slate-900" data-testid="rl-month">
              {MONTH_NAMES[cursor.getMonth()]} {cursor.getFullYear()}
            </div>
            <button
              type="button"
              onClick={() => stepMonth(1)}
              className={kit ? "inline-flex items-center justify-center" : "p-1.5 rounded-lg border border-slate-200 text-slate-600"}
              style={kit ? kitArrowStyle : undefined}
              data-testid="rl-next-month"
              aria-label="Next month"
            >
              <ChevronRight size={14} />
            </button>
          </div>

          <div className="grid grid-cols-7 gap-1 text-center text-[10px] uppercase tracking-widest text-slate-400">
            {["M", "T", "W", "T", "F", "S", "S"].map((d, i) => (
              <div key={`${d}-${i}`} className="min-w-0">{d}</div>
            ))}
          </div>

          <div className="grid grid-cols-7 gap-1" data-testid="rl-grid">
            {grid.map((day, i) => {
              if (!day) return <div key={`pad-${i}`} className="min-w-0 aspect-square" />;
              const dayEntries = byDay.get(day) || [];
              const isSelected = selectedDay === day;
              return (
                <button
                  key={day}
                  type="button"
                  onClick={() => setSelectedDay(isSelected ? null : day)}
                  disabled={dayEntries.length === 0}
                  className={`min-w-0 aspect-square overflow-hidden rounded-md border p-0.5 flex flex-col items-center justify-start text-[10px] ${
                    isSelected
                      ? "border-[#0B2E59] bg-[#0B2E5915]"
                      : dayEntries.length
                        ? "border-slate-300 bg-white"
                        : "border-slate-100 bg-slate-50 text-slate-300"
                  }`}
                  data-testid={`rl-day-${day}`}
                  aria-label={`${day}: ${dayEntries.length} entries`}
                >
                  <span className="font-mono leading-tight">{Number(day.slice(8))}</span>
                  {/* The shapes, not the colours, say what happened here. */}
                  <span className="flex flex-wrap justify-center gap-0.5 leading-none mt-0.5 min-w-0">
                    {dayEntries.slice(0, 3).map((e) => {
                      const { Icon, tint, kitTint } = kindOf(e.kind);
                      return <Icon key={e.entry_id} size={9} color={kit ? kitTint : tint} aria-hidden="true" />;
                    })}
                    {dayEntries.length > 3 && <span className="text-slate-500">+</span>}
                  </span>
                </button>
              );
            })}
          </div>

          <p className="text-xs text-slate-500" data-testid="rl-calendar-hint">
            {selectedDay
              ? `Showing ${shown.length} from ${selectedDay}. Tap the day again to show every entry.`
              : `Days with something written are outlined. Tap one to read it. ${
                  byDay.size ? "" : "Nothing in this month — use the arrows."
                }`}
          </p>
          <span className="sr-only" data-testid="rl-month-key">{cursorMonth}</span>
        </div>
      )}

      {!failed && entries.length > 0 && (
        <div className="space-y-2" data-testid="rl-list">
          {shown.length === 0 && (
            <p className="text-sm text-slate-500" data-testid="rl-filtered-empty">
              Nothing matches those filters. Turn one back on above.
            </p>
          )}
          {shown.map((entry) => (
            <LogRow
              key={entry.entry_id}
              entry={entry}
              kit={kit}
              open={openId === entry.entry_id}
              onToggle={() => setOpenId(openId === entry.entry_id ? null : entry.entry_id)}
            />
          ))}
        </div>
      )}
      </>)}
    </div>
  );

  const body = kit ? kitBody : oldBody;

  if (kit) {
    return (
      <AccordionCard
        icon={CalendarDays}
        title="Your reflection log"
        desc="Everything you've written, and what your coaches have written back, against the date it happened."
        {...(status === "loading" || status === "failed" ? { summary: "—" } : { count: entries.length })}
        toggleTestid="rl-toggle"
        defaultOpen={false}
      >
        {body}
      </AccordionCard>
    );
  }

  return (
    <SectionCard
      icon={CalendarDays}
      title="Your reflection log"
      desc="Everything you've written, and what your coaches have written back, against the date it happened."
      collapsible
      defaultOpen={false}
      count={entries.length}
      toggleTestid="rl-toggle"
    >
      {body}
    </SectionCard>
  );
}
